import { ChangeDetectorRef, Component, HostListener, Inject, OnInit, Optional, ViewChild, ElementRef, WritableSignal, signal, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ExpectFundResponseDto } from '../shared/models/expectResponseDto';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { RequestsService } from '../shared/services/requests.service';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from '../shared/services/snackbar.service';
import { BankAccount } from '../shared/models/bankAccount';
import { BankAccountService } from '../shared/services/bank-account.service';
import { ApprovalDetails } from '../shared/models/ApprovalDetails';
import { RequestWorkflowDto } from '../shared/models/request-stage';
import { ReturnReasonDialogComponent } from '../return-reason-dialog/return-reason-dialog.component';
import { RequestType, RequestTypeOrder } from '../enums/request-type.enum';
import { MatterService } from '../shared/services/matter.service';
import { AuditService } from '../shared/services/audit.service';
import { StagesLabel } from '../enums/stages.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserRole } from '../shared/models/user-role';
import { RpaStatus } from '../enums/rpa-status.enum';
import { Subject, takeUntil } from 'rxjs';
import { Currency } from '../shared/models/currency';
import { ReceiptType, applyGeneratedTransactionReference, buildExpectFundTransactionReference } from '../shared/utils/transaction-reference';

const STAGE_PARTNER = 3;
const STAGE_ACCOUNTS = 4;
const STAGE_COMPLETED = 7;

@Component({
    selector: 'xp-view-expect-fund',
    templateUrl: './view-expect-fund.component.html',
    styleUrl: './view-expect-fund.component.scss'
})
export class ViewExpectFundComponent implements OnInit, OnDestroy {
    @ViewChild('dialogContent', { read: ElementRef }) dialogContent!: ElementRef;
    currentDate: Date = new Date(Date.now());
    RequestType = RequestType;
    ExpectFundForm!: FormGroup;
    expectFundData!: ExpectFundResponseDto;
    fileNames: AttachmentData[] = [];
    displayFile: string[] = [];
    disableButtons: Boolean = false;
    bankAccount!: string;
    paymentType!: boolean;
    toggleCheck: Boolean = false;
    currencyDropdown: Currency[] = [];
    bankAccounts: BankAccount[] = [];
    filteredBankAccounts: BankAccount[] = [];
    approveBtn: string = "Accept";
    previousStages: RequestWorkflowDto[] = [];
    fileToBeDownloaded!: RequestDocAttachmentResponse;
    selectedPaymentMethod: string = '';
    paymentMethodDropdown: string[] = ['Electronic funds transfer', 'Cheque'];
    readonly receiptTypeDropdown: string[] = [ReceiptType.FasterPayment, ReceiptType.Chaps];
    /** The reference this screen last generated, so a reference the Accounts user typed is never overwritten. */
    private lastGeneratedTransactionReference = '';
    canEditFields: boolean = false;
    /** The clients on the request's matter. With more than one, the payee is ticked from them. */
    clientOptions: string[] = [];
    /** The clients ticked in the dropdown. Only the text is part of the form; it is these names joined. */
    readonly payeeClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payeeErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payee = this.ExpectFundForm?.get('name');
            return !!payee && payee.touched && payee.invalid;
        }
    };
    isAccountsStage: boolean = false;
    isDisabled = false;
    disableAtStage4 = false;
    showReturnReason: boolean = false;
    CheckedByPartner: boolean = false;
    isLoading: Boolean = false;
    isLastAuditRpaSuccessful: boolean = false;
    StagesLabel = StagesLabel;
    isDialogOpen: WritableSignal<boolean> = signal(false);
    isExpectFundLoading: WritableSignal<boolean> = signal(false);
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);
    actionstepBaseUrl: string = '';
    actionstepOverviewBaseUrl: string = '';
    actionstepMatterUrl: string = '';
    actionstepMatterOverviewUrl: string = '';
    matterNo = '';
    selectedBankAccountId: string = '';

    private readonly destroy$ = new Subject<void>();
    private readonly boundStorageHandler = this.onStorageEvent.bind(this);

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: ExpectFundResponseDto,
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
        private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private bankAccountService: BankAccountService,
        private matterService: MatterService,
        private auditService: AuditService,
        @Optional() public dialogRef: MatDialogRef<ViewExpectFundComponent>,
        private router: Router,
        private route: ActivatedRoute,
        private manageUserRoleService: ManageUserRoleService,
        private cdr: ChangeDetectorRef,
    ) {
        this.isDialogOpen.set(!!this.data);
        this.ExpectFundForm = this.formBuilder.group({
            returnReason: [''],
            matterNumber: '',
            matter: '',
            matterName: '',
            reason: '',
            amount: '',
            currency: '',
            name: ['', Validators.required],
            notesToAccounts: '',
            moniesCheckbox: [false, Validators.required],
            partnerAuthorising: '',
            dateTransaction: [new Date(), Validators.required],
            dateAuthorized: '',
            dateRequested: '',
            requestedTime: [{ value: '', disabled: true }],
            transactionTime: [{ value: '' }],
            uploadedFiles: [[]],
            paymentMethod: [''],
            receiptType: [''],
            transactionReference: [''],
            checkDate: [null],
            checkFrom: [''],
            checkNumber: [''],
            checkMemo: [''],
            checkBankName: [''],
            checkBankBranch: [''],
            lastFourNumbers: [''],
            authorizationNumber: [''],
            clientBankAccount: ['', Validators.required],
            clientOffice: '',
            confirm: [false],
            isPartnerCertified: [false],
            comments: '',
        });

        this.ExpectFundForm.get('matterName')?.disable();
        this.ExpectFundForm.get('partnerAuthorising')?.disable();
        this.ExpectFundForm.get('paymentMethod')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(paymentMethod => this.updatePaymentMethodValidators(paymentMethod));
    }

    async ngOnInit() {
        this.isExpectFundLoading.set(true);

        if (this.data) {
            this.expectFundData = this.data;
            await this.resolveCreatorPartnerApprove();
        } else {
            const id = Number(this.route.snapshot.paramMap.get('id'));

            if (!id) {
                console.error('No ID in route');
                this.snackbarService.errorSnackBar('No Id');
                this.isExpectFundLoading.set(false);
                return;
            }

            await this.requestService.GetExpectFundBasedOnId(id).then((response: ExpectFundResponseDto) => {
                this.expectFundData = response;
            });

            await this.resolveCreatorPartnerApprove();
        }

        this.approveBtn = this.expectFundData?.nextStage?.stageName || '';
        this.previousStages = this.expectFundData?.previousStages?.filter(x => x.stageOrder !== 1);

        this.initializeStageFlags();
        this.showCertificationChecked();
        this.currencyDropdown = await this.requestService.getAllCurrencies();

        this.ExpectFundForm.patchValue({
            returnReason: this.expectFundData.returnReason,
            matterNumber: this.expectFundData.matterNo,
            matter: this.expectFundData.matter,
            matterName: this.expectFundData.matterNo + " - " + this.expectFundData.matter,
            reason: this.expectFundData.paymentReason,
            amount: this.expectFundData.expectedAmount,
            currency: this.expectFundData.currencyId,
            name: this.expectFundData.payerName,
            notesToAccounts: this.expectFundData.notesToAccounts,
            moniesCheckbox: this.expectFundData.moniesReturnedClient,
            partnerAuthorising: this.expectFundData.partnerAuthorisingName,
            dateAuthorized: this.expectFundData.dateAuthorised,
            dateRequested: this.expectFundData.dateRequested,
            dateTransaction: this.expectFundData.dateTransaction ? new Date(this.expectFundData.dateTransaction) : null,
            paymentMethod: this.expectFundData.paymentMethod,
            receiptType: this.expectFundData.receiptType ?? '',
            transactionReference: this.expectFundData.transactionReference,
            checkDate: this.expectFundData.checkDate,
            checkFrom: this.expectFundData.checkFrom,
            checkNumber: this.expectFundData.checkNumber,
            checkMemo: this.expectFundData.checkMemo,
            checkBankName: this.expectFundData.checkBankName,
            checkBankBranch: this.expectFundData.checkBankBranch,
            lastFourNumbers: this.expectFundData.lastFourNumbers,
            authorizationNumber: this.expectFundData.authorizationNumber,
            clientOffice: this.expectFundData.clientOffice,
            isPartnerCertified: this.expectFundData.isPartnerCertified,
        });

        if (this.expectFundData.dateRequested) {
            const d = new Date(this.expectFundData.dateRequested);
            this.ExpectFundForm.get('requestedTime')?.setValue(this.toTimeString(d));
        }

        if (this.expectFundData?.currentStage?.stageOrder === STAGE_ACCOUNTS) {
            const now = new Date();

            this.ExpectFundForm.get('dateTransaction')?.setValue(now);
            this.ExpectFundForm.get('transactionTime')?.setValue(this.toTimeString(now));
        }

        this.configureFormControls();
        this.setupTransactionReferenceGeneration();

        if (this.dialogRef) {
            this.dialogRef.disableClose = true;
        }

        this.bankAccounts = await this.bankAccountService.getAllBankAccounts();
        this.filteredBankAccounts = this.bankAccounts;

        this.paymentType = (this.expectFundData.clientOffice?.toLowerCase() === "office");
        await this.filterBankAccounts();

        const resolvedAccount = this.resolveBankAccount(this.expectFundData.clientBankAccountId);
        this.ExpectFundForm.get('clientBankAccount')?.setValue(resolvedAccount ?? null);
        this.selectedBankAccountId = resolvedAccount?.accountId ?? '';

        [this.actionstepBaseUrl, this.actionstepOverviewBaseUrl] = await Promise.all([
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl(),
        ]);

        this.matterNo = this.expectFundData?.matterNo?.toString() || '';

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.ExpectFundForm.get('clientBankAccount')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((account: BankAccount | null) => {
                this.selectedBankAccountId = account?.accountId ?? '';
                if (this.matterNo) {
                    this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}&ac_account_id=${this.selectedBankAccountId}`;
                }
            });

        if (this.expectFundData.matterNo) {
            try {
                this.clientOptions = await this.matterService.getMatterClientNames(Number(this.expectFundData.matterNo));
            } catch {
                this.clientOptions = [];
            }
            this.syncPayeeClients();
        }

        const audits = await this.auditService.getUserAuditByRequest(this.expectFundData.id, RequestTypeOrder[RequestType.ExpectFunds]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        this.isLastAuditRpaSuccessful = latestAudit?.rpaStatus === RpaStatus.Successful;

        this.ExpectFundForm.get('moniesCheckbox')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(isMoniesReceived => {
                if (isMoniesReceived == true) {
                    // With several clients the approver ticks which of them the funds are received from.
                    this.ExpectFundForm.get('name')?.setValue(
                        this.clientOptions.length > 1 ? '' : (this.clientOptions[0] ?? 'No client found'));
                } else {
                    this.ExpectFundForm.get('name')!.reset();
                }
                this.syncPayeeClients();
            });

        window.addEventListener('storage', this.boundStorageHandler);

        this.isExpectFundLoading.set(false);
        this.cdr.detectChanges();
        this.fixDialogContentHeight();
    }

    ngAfterViewInit(): void {
        this.fixDialogContentHeight();
    }

    private fixDialogContentHeight(): void {
        setTimeout(() => {
            if (this.isDialogOpen() && this.dialogContent) {
                const contentEl = this.dialogContent.nativeElement as HTMLElement;
                const currentHeight = contentEl.offsetHeight;
                contentEl.style.height = `${currentHeight}px`;
                contentEl.style.overflowY = 'auto';
            }
        }, 50);
    }

    /** True when the approver can edit the payee and the matter has several clients to tick it from. */
    get showPayeeSelect(): boolean {
        return this.canEditFields && this.clientOptions.length > 1
            && this.ExpectFundForm.get('moniesCheckbox')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.ExpectFundForm.get('name')?.value ?? '';
        const ticked = this.payeeClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    /** Sets the text to the ticked clients, in the order they are listed. */
    onPayeeClientsChange(): void {
        const ticked = this.payeeClients.value;
        this.ExpectFundForm.get('name')?.setValue(this.clientOptions.filter(name => ticked.includes(name)).join(', '));
    }

    onPayeeSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.ExpectFundForm.get('name')?.markAsTouched();
    }

    /** Ticks the listed clients that appear in the current text. */
    private syncPayeeClients(): void {
        const payeeControl = this.ExpectFundForm.get('name');
        const padded = `, ${payeeControl?.value ?? ''}, `;
        this.payeeClients.setValue(this.clientOptions.filter(name => padded.includes(`, ${name}, `)));
    }

    private async resolveCreatorPartnerApprove(): Promise<void> {
        if (this.expectFundData.createdByRole) {
            await this.manageUserRoleService
                .getUserPermissionsByRole(this.expectFundData.createdByRole.toString())
                .then((res: UserRole) => {
                    this.expectFundData.isCreatorPartnerApprove = res.partnerApprove;

                    if (this.expectFundData.isCreatorPartnerApprove && this.expectFundData.previousStages) {
                        this.expectFundData.previousStages = this.expectFundData.previousStages.filter(stage => stage.stageOrder !== 2);
                    }
                });
        } else {
            this.expectFundData.isCreatorPartnerApprove = false;
        }
    }

    private initializeStageFlags(): void {
        const stageId = this.expectFundData?.currentStage?.stageOrder;
        this.showReturnReason = !!this.expectFundData?.returnReason;
        this.isDisabled = stageId !== STAGE_PARTNER;
        this.disableAtStage4 = stageId === STAGE_ACCOUNTS;
        this.selectedPaymentMethod = this.expectFundData.paymentMethod ?? '';
        this.canEditFields = stageId === STAGE_PARTNER;
        this.isAccountsStage = stageId === STAGE_ACCOUNTS;
        this.disableButtons = stageId === STAGE_COMPLETED;
    }

    private configureFormControls(): void {
        if (this.expectFundData?.dateAuthorised) {
            this.ExpectFundForm.get('dateAuthorized')?.disable();
        }

        if (this.expectFundData?.dateRequested) {
            this.ExpectFundForm.get('dateRequested')?.disable();
            this.ExpectFundForm.get('requestedTime')?.disable();
        }

        if (!this.isDateTransactionVisible) {
            this.ExpectFundForm.get('dateTransaction')?.clearValidators();
            this.ExpectFundForm.get('dateTransaction')?.updateValueAndValidity();
        }

        if (!this.canEditFields) {
            ['reason', 'paymentMethod', 'moniesCheckbox', 'name', 'amount', 'currency', 'notesToAccounts', 'certifiedByAccount']
                .forEach(ctrl => this.ExpectFundForm.get(ctrl)?.disable());
        }

        if (!this.isAccountsStage) {
            ['transactionReference', 'checkDate', 'checkFrom', 'checkNumber', 'checkMemo',
                'checkBankName', 'checkBankBranch', 'lastFourNumbers', 'authorizationNumber', 'receiptType']
                .forEach(ctrl => this.ExpectFundForm.get(ctrl)?.disable());
        }

        if (this.isAccountsStage && this.isElectronicFundsTransfer) {
            this.ExpectFundForm.get('receiptType')?.setValidators([Validators.required]);
            this.ExpectFundForm.get('receiptType')?.updateValueAndValidity();
        }

        if (!(this.canEditFields || this.isAccountsStage)) {
            this.ExpectFundForm.get('clientBankAccount')?.disable();
        }
    }

    get isPaymentMethodFieldsVisible(): boolean {
        return (this.expectFundData?.currentStage?.stageOrder ?? 0) >= STAGE_ACCOUNTS;
    }

    get isElectronicFundsTransfer(): boolean {
        return this.ExpectFundForm.get('paymentMethod')?.value === 'Electronic funds transfer';
    }

    get showReceiptType(): boolean {
        if (!this.isElectronicFundsTransfer || !this.isPaymentMethodFieldsVisible) {
            return false;
        }

        return this.isAccountsStage || !!this.expectFundData?.receiptType;
    }

    /**
     * Fills the Transaction Reference (TTI / FPI plus the day) from the Receipt Type and the Transaction Date,
     * for the Accounts user, who can still change it.
     */
    private setupTransactionReferenceGeneration(): void {
        // Only the Accounts user fills in the reference; at other stages the saved value is shown as it is.
        if (!this.isAccountsStage) return;

        const refresh = (): void => {
            if (!this.isElectronicFundsTransfer) return;

            const generated = buildExpectFundTransactionReference(
                this.ExpectFundForm.get('receiptType')?.value,
                this.ExpectFundForm.get('dateTransaction')?.value);

            this.lastGeneratedTransactionReference = applyGeneratedTransactionReference(
                this.ExpectFundForm.get('transactionReference'), generated, this.lastGeneratedTransactionReference);
        };

        ['receiptType', 'dateTransaction'].forEach(name =>
            this.ExpectFundForm.get(name)?.valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe(refresh));

        refresh();
    }

    get isDateTransactionVisible(): boolean {
        return (this.expectFundData?.currentStage?.stageOrder ?? 0) >= STAGE_ACCOUNTS;
    }

    private updatePaymentMethodValidators(paymentMethod: string): void {
        this.resetValidation();
        if (paymentMethod === 'Electronic funds transfer') {
            this.ExpectFundForm.get('transactionReference')?.setValidators([Validators.required]);
        }
        else if (paymentMethod === 'Cheque') {
            ['checkFrom', 'checkNumber', 'checkBankName', 'checkDate', 'transactionReference'].forEach(field => {
                this.ExpectFundForm.get(field)?.setValidators([Validators.required]);
            });
        }
    }

    private resetValidation(): void {
        ['checkDate', 'checkFrom', 'checkNumber', 'checkBankName', 'transactionReference'].forEach(field => {
            this.ExpectFundForm.get(field)?.clearValidators();
            this.ExpectFundForm.get(field)?.updateValueAndValidity();
        });
    }

    showCertificationChecked(): void {
        const stageId = this.expectFundData?.currentStage?.stageOrder;

        this.CheckedByPartner = stageId === STAGE_PARTNER || stageId === STAGE_ACCOUNTS;

        if (stageId === STAGE_PARTNER) {
            this.ExpectFundForm.get('isPartnerCertified')?.setValidators([Validators.requiredTrue]);
        }
        if (stageId === STAGE_ACCOUNTS) {
            this.ExpectFundForm.get('isPartnerCertified')?.disable();
        }
    }

    get getRequestId(): number | undefined {
        return this.expectFundData?.id;
    }

    onPaymentMethodChange(): void {
        this.selectedPaymentMethod = this.ExpectFundForm.value.paymentMethod;
    }

    private resolveBankAccount(accountId?: string): BankAccount | undefined {
        return this.bankAccounts.find(a => a.accountId === accountId);
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    get hasAttachments(): boolean {
        return this.expectFundData?.documentAvailable ?? false;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event): void {
        this.unlockPopup();
    }

    unlockPopup(): void {
        this.requestService.requestLockorUnLock(this.expectFundData.id, RequestType.ExpectFunds).then((res) => {
            if (res) {
                this.snackbarService.successSnackBar("The request has been unlocked.");
            }
        });
    }

    onFilesSelected(input: any): void {
        if (input.files) {
            this.fileNames = [];
            this.displayFile = [];
            const filesArray = Array.from(input.files);

            filesArray.forEach((file: any) => {
                const fileName = file.name;
                this.displayFile.push(fileName);
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const bytes = e.target.result.split('base64,')[1];
                    this.fileNames.push({ fileName: fileName, attachments: bytes });
                };
                reader.readAsDataURL(file);
            });
        }
        this.ExpectFundForm.get('uploadedFiles')?.setValue(this.fileNames);
    }

    closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close(true);
        }

        if (!this.isDialogOpen()) {
            window.close();
        }
    }

    clearUpload(fileInput: any): void {
        this.displayFile = [];
        this.fileNames = [];
        this.ExpectFundForm.get('uploadedFiles')?.setValue([]);
        fileInput.value = '';
    }

    uploadDoc(): void {
        if (this.ExpectFundForm.get('uploadedFiles')?.value.length > 0) {
            const docData: RequestDocAttachmentResponse = {
                requestId: this.expectFundData.id!,
                requestType: RequestType.ExpectFunds,
                attachments: this.ExpectFundForm.get('uploadedFiles')?.value
            };

            this.requestService.addRequestDocument(docData).then((res) => {
                this.snackbarService.successSnackBar(`${res}`);
            });
        }

        if (this.dialogRef) {
            this.dialogRef.close(true);
        }
    }

    onUrlCopied(): void {
        this.snackbarService.successSnackBar("URL copied to clipboard");
    }

    isReturnDisabled(stageId: number): boolean {
        if (!this.isLastAuditRpaSuccessful) {
            return false;
        }

        const currentStageId = this.expectFundData?.currentStage?.stageOrder;

        if (currentStageId === STAGE_ACCOUNTS || currentStageId === 6) {
            return false;
        }

        return stageId !== STAGE_ACCOUNTS && stageId !== 6;
    }

    private async fetchRpaStatus(): Promise<boolean> {
        const audits = await this.auditService.getUserAuditByRequest(this.expectFundData.id, RequestTypeOrder[RequestType.ExpectFunds]);
        const latestAudit = audits?.length ? audits[audits.length - 1] : null;
        return latestAudit?.rpaStatus === RpaStatus.Successful;
    }

    async returnRequest(stageId: number, stageName: string): Promise<void> {
        const rpaStatus = await this.fetchRpaStatus();
        const dialogRef = this.dialog.open(ReturnReasonDialogComponent, {
            width: '600px',
            data: {
                stageName,
                matterNo: this.expectFundData?.matterNo,
                currentStageId: this.expectFundData?.currentStage?.stageOrder,
                rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            },
            disableClose: true,
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result) {
                    this.requestService.returnExpectFund(
                        this.expectFundData.id,
                        stageId,
                        result.reason,
                        result.isRPAReversalRequested
                    ).then((res) => {
                        if (res) {
                            this.snackbarService.successSnackBar(`${res}`);
                            this.closeDialog();
                        }
                    }).catch(() => {
                        this.snackbarService.errorSnackBar("Failed to return expect fund request. Please try again.");
                    });
                }
            });
    }

    async delete(): Promise<void> {
        const rpaStatus = await this.fetchRpaStatus();
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '600px',
            data: {
                title: 'Are you sure?',
                message: 'The data associated with the request will be deleted.',
                dialogType: 'warning',
                buttonType: 'ConfirmCancel',
                confirmText: 'Confirm',
                cancelText: 'Cancel',
                matterNo: this.expectFundData?.matterNo,
                currentStageId: this.expectFundData?.currentStage?.stageOrder,
                rpaStatus,
                selectedBankAccountId: this.selectedBankAccountId,
            },
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                if (res) {
                    this.requestService.deleteExpectFund(this.expectFundData.id!)
                        .then((res) => {
                            if (res) {
                                this.snackbarService.successSnackBar(`${res}`);
                                this.closeDialog();
                            }
                        })
                        .catch(() => {
                            this.snackbarService.errorSnackBar("Failed to delete expect fund request. Please try again.");
                        });
                }
            });
    }

    approveRequest(): void {

        if (this.ExpectFundForm.invalid) {
            this.ExpectFundForm.markAllAsTouched();
            return;
        }

        const fv = this.ExpectFundForm.value;
        const rd = this.expectFundData;

        const selectedAccount = this.ExpectFundForm.get('clientBankAccount')?.value as BankAccount | null;

        const approvalDetails: ApprovalDetails = {
            dateTransaction: (() => {
                const datePart = this.ExpectFundForm.get('dateTransaction')?.value;
                if (!datePart) return undefined;

                const d = datePart instanceof Date ? datePart : new Date(datePart);
                const [hours, minutes] = (
                    this.ExpectFundForm.get('transactionTime')?.value ?? '00:00'
                ).split(':').map(Number);

                return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);
            })(),

            reasonForReceipt: this.ExpectFundForm.get('reason')?.value,
            amount: this.ExpectFundForm.get('amount')?.value,
            paymentMethod: this.ExpectFundForm.get('paymentMethod')?.value || rd.paymentMethod,
            currency: this.currencyDropdown.find(c => c.id === this.ExpectFundForm.get('currency')?.value)?.currencyCode ?? rd.currencyCode ?? '',
            payee: this.ExpectFundForm.get('name')?.value,
            clientOffice: fv.clientOffice,
            requestDate: this.ExpectFundForm.get('dateRequested')?.value,
            notesToAccounts: this.ExpectFundForm.get('notesToAccounts')?.value,
            transactionReference: fv.transactionReference || rd.transactionReference,
            receiptType: fv.receiptType || rd.receiptType,
            checkDate: fv.checkDate || rd.checkDate,
            checkFrom: fv.checkFrom || rd.checkFrom,
            checkNumber: fv.checkNumber || rd.checkNumber,
            checkMemo: fv.checkMemo || rd.checkMemo,
            checkBankName: fv.checkBankName || rd.checkBankName,
            checkBankBranch: fv.checkBankBranch || rd.checkBankBranch,
            lastFourNumbers: fv.lastFourNumbers || rd.lastFourNumbers,
            authorizationNumber: fv.authorizationNumber || rd.authorizationNumber,
            uploadedFiles: fv.uploadedFiles,
            moniesReturnedToClient: this.ExpectFundForm.get('moniesCheckbox')?.value,
            confirm: fv.confirm,
            matterNumber: fv.matterNumber?.toString(),
            clientName: rd.clientName ?? this.data?.clientName,
            isPartnerCertified: fv.isPartnerCertified,
            partnerCertifiedBy: JSON.parse(localStorage.getItem("UserDetails")!).fullName,
            returnReason: undefined,
            matter: this.ExpectFundForm.get('matter')?.value,
            comments: fv.comments,
            clientBankAccountName: selectedAccount
                ? `${selectedAccount.accountId} - ${selectedAccount.description}`
                : rd.clientBankAccountName,
            clientBankAccountId: selectedAccount?.accountId ?? rd.clientBankAccountId,
        };

        this.requestService.approveRequest(rd.id!, RequestType.ExpectFunds, this.expectFundData.nextStage?.id ?? 0, approvalDetails)
            .then((res: string) => {
                if (res) {
                    this.snackbarService.successSnackBar(`${res}`);
                    this.uploadDoc();
                    this.closeDialog();
                }
            })
            .catch(() => {
                this.snackbarService.errorSnackBar("Failed to approve expect fund request. Please try again.");
            });
    }

    filterBankAccounts(): void {
        this.filteredBankAccounts = this.bankAccounts;
    }

    downloadDocuments(requestId: number, requestType: string): void {
        this.requestService.getRequestDocumentList(requestId, requestType).then((res) => {
            this.fileToBeDownloaded = res;
            this.downloadMultipleBase64Files(this.fileToBeDownloaded.attachments ?? []);
        });
    }

    downloadMultipleBase64Files(files: AttachmentData[]): void {
        files.forEach(file => {
            const { attachments, fileName } = file;
            const byteCharacters = atob(attachments);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        });
    }

    @HostListener('window:beforeunload')
    onExpandedTabClose(): void {
        localStorage.setItem('receiptExpandedClosed', Date.now().toString());
    }

    openInNewTab(): void {
        const id = this.expectFundData?.id;
        if (!id) return;

        localStorage.setItem('receiptExpandedOpen', Date.now().toString());

        const url = this.router.serializeUrl(
            this.router.createUrlTree(['/approvals', 'expect-funds-request', id])
        );

        window.open(window.location.origin + url, '_blank');
    }

    onStorageEvent(event: StorageEvent): void {
        if (event.key === 'receiptExpandedClosed') {
            this.closeDialog();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.removeEventListener('storage', this.boundStorageHandler);
    }
}
